import { apiSlice } from '../apiSlice';
import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	ChartletFilter,
	GameCartoonStickerSeriesDTO,
	GameCartoonStickerSeriesDTORequest,
	GameCartoonStickerTreasureDTO,
	PublishDTO,
	TreasureFilter,
} from '../DTO/treasury/chartlet.interface';

export const chartletApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getChartletPage: builder.query<
			SuccessResponse<Paginated<GameCartoonStickerSeriesDTO>>,
			ChartletFilter
		>({
			query: (body) => ({
				url: `/web/game/cartoon-sticker/series/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['ChartletList'],
		}),
		getChartletSortList: builder.query<
			SuccessResponse<GameCartoonStickerSeriesDTO[]>,
			null
		>({
			query: () => ({
				url: `/web/game/cartoon-sticker/series/sort`,
				method: 'GET',
			}),
		}),
		sortChartlet: builder.mutation<SuccessResponse<any>, string[]>({
			query: (body) => ({
				url: `/web/game/cartoon-sticker/series/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['ChartletList'],
		}),
		createChartlet: builder.mutation<
			SuccessResponse<any>,
			GameCartoonStickerSeriesDTORequest
		>({
			query: (body) => ({
				url: `/web/game/cartoon-sticker`,
				method: 'POST',
				body,
			}),
		}),
		updateChartlet: builder.mutation<
			SuccessResponse<any>,
			GameCartoonStickerSeriesDTORequest
		>({
			query: (body) => ({
				url: `/web/game/cartoon-sticker/series/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['ChartletDetail'],
		}),
		setChartletStatus: builder.mutation<
			SuccessResponse<any>,
			{ publishList: PublishDTO[] }
		>({
			query: (body) => ({
				url: `/web/game/cartoon-sticker/series/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['ChartletList', 'ChartletDetail'],
		}),
		getChartletDetail: builder.query<
			SuccessResponse<GameCartoonStickerSeriesDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/game/cartoon-sticker/series/${id}`,
				method: 'GET',
			}),
			providesTags: ['ChartletDetail'],
		}),
		getTreasurePage: builder.query<
			SuccessResponse<Paginated<GameCartoonStickerTreasureDTO>>,
			TreasureFilter
		>({
			query: (body) => ({
				url: `/web/game/cartoon-sticker/series/${body.seriesId}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['TreasureList'],
		}),
		getTreasureSortList: builder.query<
			SuccessResponse<GameCartoonStickerTreasureDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/game/cartoon-sticker/series/${id}/sort`,
				method: 'GET',
			}),
		}),
		sortTreasure: builder.mutation<SuccessResponse<any>, string[]>({
			query: (body) => ({
				url: `/web/game/cartoon-sticker/treasure/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['TreasureList'],
		}),
		setTreasureStatus: builder.mutation<
			SuccessResponse<any>,
			{ publishList: PublishDTO[]; id: string }
		>({
			query: (body) => ({
				url: `/web/game/cartoon-sticker/treasure/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['TreasureList', 'TreasureDetail'],
		}),
		deleteChartlet: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/game/cartoon-sticker/series/${id}`,
				method: 'DELETE',
			}),
		}),
		getTreasureDetail: builder.query<
			SuccessResponse<GameCartoonStickerTreasureDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/game/cartoon-sticker/treasure/${id}`,
				method: 'GET',
			}),
			providesTags: ['TreasureDetail'],
		}),
		updateTreasureDetail: builder.mutation<
			SuccessResponse<any>,
			GameCartoonStickerTreasureDTO
		>({
			query: (body) => ({
				url: `/web/game/cartoon-sticker/treasure/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['TreasureDetail'],
		}),
		createTreasure: builder.mutation<
			SuccessResponse<any>,
			GameCartoonStickerTreasureDTO
		>({
			query: (body) => ({
				url: `/web/game/cartoon-sticker/series/${body.chartletId}`,
				method: 'POST',
				body,
			}),
		}),
		deleteTreasure: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/game/cartoon-sticker/treasure/${id}`,
				method: 'DELETE',
			}),
		}),
	}),
});

export const {
	useGetChartletPageQuery,
	useGetChartletSortListQuery,
	useSortChartletMutation,
	useCreateChartletMutation,
	useUpdateChartletMutation,
	useSetChartletStatusMutation,
	useGetChartletDetailQuery,
	useGetTreasurePageQuery,
	useGetTreasureSortListQuery,
	useSortTreasureMutation,
	useSetTreasureStatusMutation,
	useDeleteChartletMutation,
	useGetTreasureDetailQuery,
	useUpdateTreasureDetailMutation,
	useCreateTreasureMutation,
	useDeleteTreasureMutation,
} = chartletApiSlice;
